import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Chip, CircularProgress, Collapse, Divider, IconButton, keyframes,
  Link, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack, TextField, Tooltip,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AddCommentIcon from '@mui/icons-material/AddComment';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

import {
  assistant, type AssistantHealth, type AssistantMessage, type AssistantQuestion,
  type AssistantSession,
} from '../api/assistant';
import { AssistantQuestions } from './AssistantQuestions';
import type { PlanRequest, RoutePlan } from '../api/types';

/**
 * The planning assistant, beside the form rather than instead of it.
 *
 * Everything it does goes through the same request the form builds, so the
 * two are never describing different trips: it answers with a draft and a
 * plan, the page adopts both, and the traveller can carry on by hand from
 * wherever the conversation left them. That is the whole design — a second
 * way to fill in one form, not a second planner.
 */

/** What each tool did, in the words an operator would use for it. */
const TOOL_LABELS: Record<string, string> = {
  list_cities: 'Looked up cities',
  list_route_styles: 'Read the route styles',
  list_categories: 'Counted what is around',
  search_places: 'Searched places',
  cities_on_route: 'Checked the cities crossed',
  set_plan_basics: 'Set the tasking',
  add_must_visit: 'Added a stop',
  remove_must_visit: 'Dropped a stop',
  avoid: 'Set something to avoid',
  set_leg_mode: 'Changed a leg',
  set_break: 'Set a break',
  suggest_stops: 'Ranked what is worth stopping for',
  plan_route: 'Planned the route',
  show_draft: 'Checked the tasking',
  save_plan: 'Saved it',
};

/**
 * Openers, written about the city that is actually selected.
 *
 * Each one has to say what the round is FOR: an opener naming only a start
 * and an avoidance rule produced an empty loop, since nothing told the
 * planner what to draw stops from.
 *
 * None of them names where it begins, and that is deliberate — the
 * assistant asks for the departure point, the set-off time and the hours
 * before it plans anything, because those are the operator's to give. An
 * opener that supplied a start would be answering on their behalf, which is
 * the one thing the assistant is built not to do.
 *
 * Two of them, and the first carries no avoidance clause. "Holding clear of
 * schools" is a fair thing to ask for and a poor thing to open with: 306
 * schools in Washington DC is 308 exclusion zones, they cover the ground the
 * round would have used, and the first thing an opener did was explain why
 * it could not be planned. Avoidance is one sentence away once there is a
 * round to narrow.
 *
 * An emphasis is still chosen by naming one — "a route survey", "a mass
 * evacuation" — which the assistant sets before planning.
 */
const openersFor = (city: string) => [
  // Plain about the kinds of site and nothing else. "Infrastructure check"
  // read as the NAME of an emphasis — the model looked for one, found none
  // by that name, and spent its answer asking which emphasis to use over a
  // route it had already planned.
  `Check hospitals, police and fire stations across ${city} by car`,
  // Says what it visits, for the same reason the first one does: "welfare
  // round" names no kind of site, so the planner has nothing to draw stops
  // from and the answer becomes a question about interests.
  `Half a day welfare round in ${city} by car — community halls and clinics — with a meal stop and a rest stop`,
];/** Three dots, while it works. */
const blink = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
`;

function Thinking() {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', px: 1.5, py: 1 }}>
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          sx={{
            width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.secondary',
            animation: `${blink} 1.4s infinite`, animationDelay: `${index * 0.2}s`,
          }}
        />
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
        Working — a plan takes a few seconds
      </Typography>
    </Stack>
  );
}

/**
 * Light formatting for what the model writes.
 *
 * It answers in the markdown it was trained on — **bold** and dashed lists —
 * and rendering that as literal asterisks makes a careful answer look broken.
 * Deliberately not a markdown library: this is three rules, and everything
 * else is shown as typed.
 */
function MessageText({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <Box>
      {lines.map((line, index) => {
        const bullet = /^\s*[-*]\s+/.test(line);
        const content = line.replace(/^\s*[-*]\s+/, '');
        // Bold and links. A bare URL in a chat bubble that cannot be clicked
        // is worse than no URL — the whole point of offering one is that
        // somebody can go and look.
        const parts = content.split(/(\*\*[^*]+\*\*|https?:\/\/[^\s)]+)/g).filter(Boolean);
        if (!line.trim()) return <Box key={index} sx={{ height: 6 }} />;
        return (
          <Typography
            key={index}
            variant="body2"
            sx={{ pl: bullet ? 1.5 : 0, textIndent: bullet ? '-0.7em' : 0 }}
          >
            {bullet ? '• ' : ''}
            {parts.map((part, i) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return (
                  <Box component="strong" key={i} sx={{ fontWeight: 600 }}>
                    {part.slice(2, -2)}
                  </Box>
                );
              }
              if (/^https?:\/\//.test(part)) {
                return (
                  <Link
                    key={i}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ wordBreak: 'break-all' }}
                  >
                    {part.includes('google.com/search') ? 'check on Google' : part}
                  </Link>
                );
              }
              return <Box component="span" key={i}>{part}</Box>;
            })}
          </Typography>
        );
      })}
    </Box>
  );
}

/** What the agent did, foldable, because it is evidence rather than chat. */
function ToolTrail({ calls }: { calls: { name: string; args: unknown; result: string }[] }) {
  const [open, setOpen] = useState(false);
  if (calls.length === 0) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ alignItems: 'center', cursor: 'pointer' }}
        onClick={() => setOpen((current) => !current)}
      >
        <BuildIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          {calls.length} step{calls.length === 1 ? '' : 's'}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 16, color: 'text.secondary',
            transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s',
          }}
        />
      </Stack>
      <Collapse in={open}>
        <Stack spacing={0.75} sx={{ mt: 0.75 }}>
          {calls.map((call, index) => (
            <Box key={index}>
              <Chip
                size="small"
                variant="outlined"
                label={TOOL_LABELS[call.name] ?? call.name}
                sx={{ mb: 0.25 }}
              />
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  m: 0, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'action.hover',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  color: 'text.secondary', maxHeight: 140, overflow: 'auto',
                }}
              >
                {call.result || '—'}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

/** The plan the last answer produced, as a line worth glancing at. */
function PlanSummary({ plan }: { plan: RoutePlan }) {
  const hours = Math.floor(plan.totals.totalMinutes / 60);
  const minutes = plan.totals.totalMinutes % 60;
  return (
    <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
      <Chip size="small" color="primary" variant="outlined" label={`${plan.stops.length} stops`} />
      <Chip
        size="small" color="primary" variant="outlined"
        label={hours ? `${hours} hr ${minutes} min` : `${minutes} min`}
      />
      <Chip
        size="small" color="primary" variant="outlined"
        label={`${(plan.totals.distanceMeters / 1000).toFixed(1)} km`}
      />
      <Chip size="small" variant="outlined" label="drawn on the map" />
    </Stack>
  );
}

export function AssistantPanel({
  cityId, cityName, draft, plan, onApply, onTrip, formVisible, onToggleForm,
  onNewConversation, onClose,
}: {
  cityId: string | null;
  /** Named in the openers, so they describe the city on screen. */
  cityName: string;
  /** What the form currently says, sent so the assistant edits THIS trip. */
  draft: PlanRequest | null;
  /** What is drawn now, so a reply can talk about the plan on screen. */
  plan: RoutePlan | null;
  /** Hands back what the assistant built, for the page to draw. */
  /**
   * Hands a conversation's trip to the page.
   *
   * `restore` marks the ones that are not an answer to anything — reopening
   * a conversation, or picking up the one that was already open. The page
   * drops a turn's answer when the form was cleared while it was thinking;
   * a restore is the operator asking for that trip by name, so it is never
   * dropped.
   */
  onApply: (
    draft: Partial<PlanRequest>,
    plan: RoutePlan | null,
    options?: { restore?: boolean; startedAt?: number },
  ) => void;
  /** A trip belongs to the multi-day planner; the page switches to it. */
  onTrip?: (tripRequest: unknown, trip: unknown) => void;
  /** Whether the form beside the map is showing, and how to fold it away. */
  formVisible?: boolean;
  onToggleForm?: () => void;
  /**
   * Fired when a new conversation starts, so the page can clear the day.
   *
   * "New conversation" that left the last one's route on the map and its
   * places in the form was only half a new conversation: the assistant had
   * forgotten the trip and the screen had not.
   */
  onNewConversation?: () => void;
  onClose?: () => void;
}) {
  const [health, setHealth] = useState<AssistantHealth | null>(null);
  const [session, setSession] = useState<AssistantSession | null>(null);
  const [sessions, setSessions] = useState<AssistantSession[]>([]);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [lastPlan, setLastPlan] = useState<RoutePlan | null>(null);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  /*
   * What the assistant is waiting to be told, drawn as a form under the reply.
   *
   * Held here rather than on the message it came with, because it is the
   * CURRENT question: answering it, typing instead, or starting a new
   * conversation all clear it, and an old form left under an old message
   * would invite an answer to something already settled.
   */
  const [questions, setQuestions] = useState<AssistantQuestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  /*
   * Whether this conversation is following the form.
   *
   * A NEW conversation must not inherit the last one's day. The panel sends
   * the form's draft with every message so that "avoid that one" means what
   * is on screen — but on a fresh session that same wire carried the old
   * start, destination and avoidance into a conversation that had said
   * nothing yet, and the assistant answered about a trip nobody had
   * described. So a fresh session starts blank and begins following the form
   * again once it has a day of its own.
   *
   * It DEFAULTED to on, which quietly undid all of that for the one case the
   * comment above was written about. The panel opens the most recent
   * conversation on mount, and with this on it then pushed the page's current
   * trip into it — so a conversation that had never planned anything acquired
   * the last one's route as its own, and answered its first message with "the
   * plan comes back the same". Off until something says otherwise.
   */
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    assistant.health().then(setHealth).catch(() => setHealth(null));
  }, []);

  const loadSessions = useCallback(() => {
    assistant.sessions().then((r) => setSessions(r.sessions)).catch(() => setSessions([]));
  }, []);

  /*
   * One conversation, resumed. A new one only when asked for.
   *
   * This used to create a session whenever it could not match one for the
   * current city — which meant an empty conversation per mount, and deleting
   * the open one immediately made another. Now: take the most recent
   * conversation this browser has, whatever city it was for, and create only
   * when there are genuinely none. The ref makes that true even if the effect
   * runs twice, which React does in development.
   */
  const creating = useRef(false);
  /*
   * The page's own plan, read without re-running the effect that opens a
   * conversation — which must run once per city, not once per re-plan.
   */
  const planRef = useRef(plan);
  planRef.current = plan;
  /*
   * The conversation comes back with its route, not as a transcript about a
   * map that shows nothing.
   *
   * Watching the session rather than doing it where the session is loaded:
   * that effect guards itself with a ref so it runs once, and React mounts
   * twice in development — so the run that fetched was also the run that was
   * cancelled, and the restore never happened. This fires whenever a session
   * lands, whichever run loaded it.
   *
   * Only onto an empty map, and only once: a plan the operator is already
   * working on is theirs, and a conversation opening underneath them is no
   * reason to replace it.
   */
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !session) return;

    /*
     * A conversation about a WEEK comes back as a week.
     *
     * This only ever restored the single-day plan, and a session holding a
     * trip holds both — so reopening a conversation that had planned three
     * days put one day on the map and left the transcript beside it
     * describing the other two. The trip was sitting in the session the whole
     * time, ignored because nothing here looked for it.
     *
     * Taken first, because it is the larger claim: a session with a trip is a
     * trip, whatever single day was planned on the way to it. On the trip
     * screen this fills it in place; on the day screen the page switches
     * planner, which is what the same hand-off does when a trip is asked for
     * rather than reopened.
     */
    if (session.trip && session.trip_draft && onTrip) {
      restored.current = true;
      onTrip(session.trip_draft, session.trip);

      return;
    }

    if (!session.plan || planRef.current) return;
    restored.current = true;
    onApply(session.draft, session.plan, { restore: true });
  }, [session, onApply, onTrip]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { sessions: existing } = await assistant.sessions();
        if (cancelled) return;
        setSessions(existing);
        const mine = existing.find((s) => s.city_id === cityId) ?? existing[0];

        /*
         * Reading is safe to repeat; only CREATING has to happen once.
         *
         * The guard used to cover the whole effect, and React mounts twice in
         * development: the first run claimed the guard and was then cancelled
         * before it opened anything, and the second returned at the door. The
         * result was a panel that never loaded its conversation at all — an
         * empty transcript over a map with no route, for a conversation that
         * had both.
         */
        if (mine) {
          const opened = await assistant.open(mine.id);
          if (cancelled) return;
          setSession(opened.session);
          setMessages(opened.messages.filter((m) => m.role !== 'system'));
          setQuestions(opened.questions ?? []);
          setLastPlan(opened.session.plan);
          /*
           * Only a conversation with a trip of its own follows the form.
           *
           * Following means pushing whatever is on screen into this session,
           * and what is on screen at mount belongs to whoever put it there —
           * often the conversation before this one. Pushed into an empty
           * session it became that session's "previous plan", and its first
           * answer opened with "the plan comes back the same" about a route
           * it had never built.
           */
          setFollowing(Boolean(opened.session.plan));
          return;
        }

        if (creating.current) return;
        creating.current = true;
        const started = await assistant.start(cityId);
        if (cancelled) return;
        setSession(started.session);
        setMessages([]);
        setLastPlan(null);
        // Blank, and blank means blank: nothing on screen is its doing yet.
        setFollowing(false);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [cityId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [messages, thinking]);

  /*
   * What the traveller has done by hand, told to the assistant.
   *
   * Debounced and fire-and-forget: it is context for the next question, not
   * something anybody is waiting on, and a failure here should never stop
   * somebody planning.
   */
  useEffect(() => {
    if (!session) return;
    if (!following) return;
    const timer = setTimeout(() => {
      void assistant.pushState(session.id, { ...(draft ? { draft } : {}), plan }).catch(() => {});
    }, 1200);
    return () => clearTimeout(timer);
  }, [session, draft, plan, following]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || thinking) return;
    setInput('');
    setError(null);
    /*
     * When this turn began, reported with its answer.
     *
     * The page drops an answer to a question the traveller has since thrown
     * away, and it needs this to tell that from an answer to a question they
     * asked afterwards. Without it the page could only compare against the
     * clock, so everything said in the couple of minutes following a reset
     * was discarded — including the plan they had just asked for.
     */
    const startedAt = Date.now();
    setMessages((current) => [
      ...current,
      { id: Date.now(), role: 'human', content: trimmed, tool_calls: null, created_at: '' },
    ]);
    // Whatever was being asked has now been answered, or replaced by
    // something typed. Either way the form goes before the turn runs, so
    // it is never on screen beside a reply that has moved past it.
    setQuestions([]);
    setThinking(true);
    try {
      /*
       * The conversation is created here if it does not exist yet.
       *
       * Clicking a suggestion in the first second used to do nothing at all:
       * the session was still being fetched, the guard returned early, and
       * nothing on screen said why. A click has to be an instruction, so the
       * session is opened on the way rather than being a precondition.
       */
      let current = session;
      if (!current) {
        const opened = await assistant.start(cityId);
        current = opened.session;
        setSession(current);
      }
      const reply = await assistant.send(
        current.id, trimmed, following ? draft ?? undefined : undefined,
      );
      // It has a day of its own now, so tracking the form is right again.
      setFollowing(true);
      // A trip is a different planner. Handed over rather than drawn here,
      // and only when this turn actually produced one.
      if (reply.trip && onTrip) onTrip(reply.tripRequest, reply.trip);
      setMessages((current) => [
        ...current,
        {
          id: Date.now() + 1, role: 'ai', content: reply.reply,
          tool_calls: reply.toolCalls, created_at: '',
        },
      ]);
      // What it is waiting to be told, if anything.
      setQuestions(reply.questions ?? []);
      setLastPlan(reply.plan);
      // Adopted whether or not a plan came back: a turn that only set the
      // destination has still changed the trip, and the page should show it.
      onApply(reply.draft, reply.plan, { startedAt });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setThinking(false);
    }
  }, [session, cityId, thinking, draft, following, onApply, onTrip]);

  const startFresh = useCallback(async () => {
    const opened = await assistant.start(cityId);
    setSession(opened.session);
    setMessages([]);
    setLastPlan(null);
    setQuestions([]);
    setError(null);
    // Blank means blank: neither the form's current day nor the map's route
    // belongs to this conversation.
    setFollowing(false);
    onNewConversation?.();
    loadSessions();
  }, [cityId, loadSessions, onNewConversation]);

  const resume = useCallback(async (id: string) => {
    setHistoryAnchor(null);
    const opened = await assistant.open(id);
    setSession(opened.session);
    setMessages(opened.messages.filter((m) => m.role !== 'system'));
    setQuestions(opened.questions ?? []);
    setLastPlan(opened.session.plan);
    setFollowing(true);
    if (opened.session.plan) {
      onApply(opened.session.draft, opened.session.plan, { restore: true });
    }
  }, [onApply]);

  /*
   * Deleting the open conversation opens the next one rather than starting
   * a fresh one.
   *
   * Tidying up should not leave something new behind: clearing out a list of
   * old conversations one by one used to create a replacement each time, so
   * the list never got shorter.
   */
  const forget = useCallback(async (id: string) => {
    await assistant.remove(id).catch(() => {});
    const { sessions: left } = await assistant.sessions().catch(() => ({ sessions: [] }));
    setSessions(left);
    if (session?.id !== id) return;
    const next = left.find((s) => s.city_id === cityId) ?? left[0];
    if (!next) {
      const opened = await assistant.start(cityId);
      setSession(opened.session);
      setMessages([]);
      setLastPlan(null);
      return;
    }
    const opened = await assistant.open(next.id);
    setSession(opened.session);
    setMessages(opened.messages.filter((m) => m.role !== 'system'));
    setQuestions(opened.questions ?? []);
    setLastPlan(opened.session.plan);
  }, [session, cityId]);

  const unavailable = Boolean(health && !health.ready);
  const statusColour = useMemo(() => {
    if (!health) return 'warning.main';
    return health.ready ? 'success.main' : 'warning.main';
  }, [health]);

  return (
    <Paper
      variant="outlined"
      elevation={0}
      /*
       * As tall as the window sensibly allows.
       *
       * A conversation that plans a day is long — a question, an itinerary,
       * a change — and a short box showed two messages and a scrollbar. The
       * height is taken from the viewport rather than fixed so it fits a
       * laptop and a large screen without either being wasted, and the
       * message list is the part that flexes: the header and the composer
       * stay put.
       */
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 2,
        height: { xs: 'calc(100vh - 150px)', sm: 'min(78vh, 780px)' },
        boxShadow: 6,
        bgcolor: 'background.paper',
      }}
    >
      {/* ── header ───────────────────────────────────────────────── */}
      <Stack
        direction="row"
        spacing={1.25}
        sx={{
          alignItems: 'center', px: 1.5, py: 1.25,
          bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider',
        }}
      >
        <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.main' }}>
          <AutoAwesomeIcon sx={{ fontSize: 17 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>Planning assistant</Typography>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: statusColour }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {health ? `${health.provider} · ${health.model}` : 'checking…'}
            </Typography>
          </Stack>
        </Box>
        {onToggleForm && (
          <Tooltip title={formVisible ? 'Widen the map' : 'Show the form'}>
            <IconButton size="small" onClick={onToggleForm}>
              {formVisible
                ? <OpenInFullIcon sx={{ fontSize: 18 }} />
                : <CloseFullscreenIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="New conversation">
          <span>
            <IconButton size="small" onClick={() => void startFresh()} disabled={thinking}>
              <AddCommentIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Earlier conversations">
          <span>
            <IconButton
              size="small"
              onClick={(event) => { loadSessions(); setHistoryAnchor(event.currentTarget); }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {onClose && (
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
      </Stack>

      <Menu
        anchorEl={historyAnchor}
        open={Boolean(historyAnchor)}
        onClose={() => setHistoryAnchor(null)}
        slotProps={{ paper: { sx: { maxWidth: 360 } } }}
      >
        {sessions.length === 0 && <MenuItem disabled>Nothing yet</MenuItem>}
        {sessions.map((item) => (
          <MenuItem
            key={item.id}
            selected={item.id === session?.id}
            onClick={() => void resume(item.id)}
          >
            <ListItemText
              primary={item.title ?? 'Untitled'}
              secondary={new Date(item.updated_at).toLocaleString()}
              slotProps={{
                primary: { noWrap: true, variant: 'body2' },
                secondary: { variant: 'caption' },
              }}
            />
            <ListItemIcon sx={{ minWidth: 0, ml: 1 }}>
              <IconButton
                size="small"
                onClick={(event) => { event.stopPropagation(); void forget(item.id); }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemIcon>
          </MenuItem>
        ))}
      </Menu>

      {/* ── conversation ─────────────────────────────────────────── */}
      <Box
        sx={{
          px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5,
          // The one part that grows. minHeight:0 is what lets it actually
          // scroll inside a flex column rather than pushing the composer off.
          flex: 1, minHeight: 0, overflowY: 'auto',
        }}
      >
        {unavailable && (
          <Alert severity="info" variant="outlined">
            {!health?.enabled
              ? 'The assistant is switched off. Turn it on in Admin → Planning assistant.'
              : 'No model to talk to. Add an API key for the chosen provider, or point '
                + 'it at a local Ollama, in Admin → Planning assistant.'}
          </Alert>
        )}

        {messages.length === 0 && !unavailable && (
          <Box>
            {/*
              A conversation with no messages, over a day that is already on
              screen, greeted the operator with "describe the tasking" — as
              though it could not see the route in front of it. It can: the
              draft and the plan are pushed to the session as they change, and
              the draft rides along with the first message. It simply never
              said so, and an assistant that looks ignorant of the map is one
              nobody asks about the map.

              So when there is a plan, it is named, and the suggestions become
              changes TO it rather than taskings that would replace it.
            */}
            {plan ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  I can see the day on screen — {plan.stops.length} stops,{' '}
                  {Math.floor(plan.totals.totalMinutes / 60)
                    ? `${Math.floor(plan.totals.totalMinutes / 60)} hr `
                    : ''}
                  {plan.totals.totalMinutes % 60} min,{' '}
                  {(plan.totals.distanceMeters / 1000).toFixed(1)} km. Ask me to
                  change it, or describe a different tasking entirely.
                </Typography>
                <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                  {[
                    'Make it shorter',
                    'Add a meal stop',
                    'Keep it clear of schools',
                  ].map((opener) => (
                    <Chip
                      key={opener}
                      label={opener}
                      variant="outlined"
                      disabled={thinking}
                      onClick={() => void send(opener)}
                      sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.75,
                        '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' } }}
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Or start something new:
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Describe the tasking and I will assemble it here. Everything I do goes
                into the form below, so you can take it over whenever you like.
              </Typography>
            )}
            <Stack spacing={0.75} sx={{ mt: plan ? 0.75 : 0 }}>
              {openersFor(cityName).map((opener) => (
                <Chip
                  key={opener}
                  label={opener}
                  variant="outlined"
                  disabled={thinking}
                  onClick={() => void send(opener)}
                  sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.75,
                    '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' } }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {messages.map((message, index) => {
          const mine = message.role === 'human';
          const isLast = index === messages.length - 1;
          return (
            <Stack
              key={message.id}
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'flex-start',
                flexDirection: mine ? 'row-reverse' : 'row',
              }}
            >
              <Avatar
                sx={{
                  width: 26, height: 26,
                  bgcolor: mine ? 'grey.600' : 'primary.main',
                }}
              >
                {mine
                  ? <PersonIcon sx={{ fontSize: 15 }} />
                  : <AutoAwesomeIcon sx={{ fontSize: 15 }} />}
              </Avatar>
              <Box
                sx={{
                  maxWidth: '86%',
                  px: 1.5, py: 1,
                  borderRadius: 2,
                  borderTopRightRadius: mine ? 4 : 16,
                  borderTopLeftRadius: mine ? 16 : 4,
                  bgcolor: mine ? 'primary.main' : 'action.hover',
                  color: mine ? 'primary.contrastText' : 'text.primary',
                }}
              >
                <MessageText text={message.content} />
                {!mine && message.tool_calls && <ToolTrail calls={message.tool_calls} />}
                {!mine && isLast && lastPlan && <PlanSummary plan={lastPlan} />}
              </Box>
            </Stack>
          );
        })}

        {thinking && <Thinking />}
        <div ref={endRef} />
      </Box>

      {error && (
        <Alert severity="warning" variant="outlined" sx={{ mx: 1.5, mb: 1 }}>{error}</Alert>
      )}

      {/*
        ── what it is waiting to be told ──────────────────────────

        Above the composer rather than inside the transcript, so it stays put
        while the conversation scrolls behind it: a question that has scrolled
        out of sight is a question that reads as unanswered on both sides.

        The composer stays live underneath. A form is the quickest way to
        answer what was asked and never the only one — somebody who wants to
        say something else entirely should not have to dismiss a question
        first, and typing clears it anyway.
      */}
      {questions.length > 0 && (
        <Box sx={{ px: 1.25, pb: 1 }}>
          <AssistantQuestions
            questions={questions}
            cityId={cityId}
            busy={thinking}
            onAnswer={(answer) => void send(answer)}
            onDismiss={() => setQuestions([])}
          />
        </Box>
      )}

      <Divider />

      {/* ── composer ─────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1} sx={{ p: 1.25, alignItems: 'flex-end' }}>
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={5}
          placeholder="Describe the tasking, or a change to this one"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            // Enter sends; Shift+Enter is a new line, as every chat box works.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void send(input);
            }
          }}
          disabled={unavailable || !session}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        />
        <Tooltip title="Send · Shift+Enter for a new line">
          <span>
            <IconButton
              color="primary"
              onClick={() => void send(input)}
              disabled={!input.trim() || thinking || unavailable || !session}
              sx={{ mb: 0.25 }}
            >
              {thinking ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}
